/**
 * La navigazione.
 *
 * Le rotte del pannello **non sono scritte qui**: sono generate da
 * `config/navigation.ts`, che è anche ciò che disegna il menu. È la garanzia
 * strutturale contro il difetto 11 del sistema di progetto — una voce di menu
 * che non porta da nessuna parte, o una rotta che nessun menu raggiunge, non
 * sono errori da cercare: sono stati impossibili.
 *
 * Qui restano le sole rotte che un menu non può contenere: il primo avvio,
 * l'ingresso, il cambio password obbligato, il profilo, il rifiuto e il non
 * trovato.
 */

import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';
import { NAV, voceApribile } from '@/config/navigation';
import { useAuthStore } from '@/stores/auth';
import { onSessionePersa } from '@/lib/http';
import { leggiStatoImpianto, segnaImpiantoConfigurato } from '@/lib/primo-avvio';
import { versioneNuova } from '@/lib/versione-nuova';

/**
 * Le rotte del contenuto, ricavate dal menu.
 *
 * Ognuna porta in `meta` i permessi della sua voce e l'etichetta: la guardia
 * legge i primi, l'intestazione della pagina e il titolo del documento leggono
 * la seconda. Nessuno dei due li ripete a mano.
 */
const rotteDelMenu: RouteRecordRaw[] = NAV.flatMap((sezione) =>
  sezione.voci.map((voce) => ({
    path: voce.path,
    name: voce.name,
    component: voce.component,
    meta: {
      /*
        ⚠ **Qui non si ricopiano più le due condizioni, si porta il nome.**

        C'erano `permessi: voce.permessi` e `visibile: voce.visibile`, e la guardia in fondo a
        questo file le rileggeva una per una. Funzionava, ed era il **secondo padrone** del fatto
        «questa voce si vede» (§116): tre posti lo decidevano — il menu, la home del Cruscotto e
        questa guardia — e il 14 ottobre due di loro dicevano cose diverse, con la home che
        offriva voci che il menu aveva chiuso. Adesso la domanda la fa `voceVisibile`, e qui
        viaggia soltanto il nome con cui chiederglielo.
      */
      chiave: voce.name,
      titolo: voce.label,
      voce: voce.name,
    },
    children: (voce.figlie ?? []).map((figlia) => ({
      path: figlia.path,
      name: figlia.name,
      component: figlia.component,
      meta: {
        // Il nome **della figlia**, non del genitore: le sue due condizioni — i permessi suoi
        // se ce li ha, e sempre la condizione del genitore — le risolve `voceVisibile`, dove
        // stanno scritte una volta.
        chiave: figlia.name,
        titolo: figlia.label,
        // `voce` resta del **genitore**: è quella che si accende nella barra laterale, ed è
        // un'altra domanda da «chi può entrare».
        voce: voce.name,
      },
    })),
  })),
);

const routes: RouteRecordRaw[] = [
  {
    path: '/entra',
    name: 'entra',
    component: () => import('@/views/auth/IngressoView.vue'),
    meta: { pubblica: true, titolo: 'Entra' },
  },
  {
    // Accanto all'ingresso e non fra le rotte del menu: non è una voce di menu,
    // è la schermata che si vede *prima* che esista qualcuno a cui un menu
    // possa servire. Generarla da `config/navigation.ts` significherebbe una
    // voce «Primo avvio» nella barra laterale di un impianto già configurato,
    // cioè una porta murata in bella vista.
    //
    // `pubblica` per necessità — è l'unica cosa raggiungibile senza sessione su
    // un impianto vuoto — e per conseguenza utile: `App.vue` disegna senza
    // cornice tutto ciò che è pubblico, e una barra laterale piena di voci che
    // non si aprono sarebbe il primo messaggio che il pannello dà di sé.
    path: '/primo-avvio',
    name: 'primo-avvio',
    component: () => import('@/views/setup/PrimoAvvioView.vue'),
    meta: { pubblica: true, titolo: 'Primo avvio' },
  },
  {
    /*
      L'informativa privacy, pubblica per necessità: gli store la vogliono a un indirizzo
      che apra **senza account**, e chi la apre è un revisore di Apple o di Google, non un
      dipendente. `pubblica: true` come l'ingresso, quindi niente barra laterale e nessuna
      chiamata all'API. Il testo sta dentro la vista e si sostituisce lì quando il titolare
      lo approva: due copie della stessa informativa divergono al primo ritocco.
    */
    path: '/privacy',
    name: 'privacy',
    component: () => import('@/views/auth/PrivacyView.vue'),
    meta: { pubblica: true, titolo: 'Informativa privacy' },
  },
  {
    /*
      La risposta a un invito di riunione, dal link nella mail. Chi la apre spesso **non ha un
      account** — un fornitore, un cliente, un dipendente senza l'app — quindi `pubblica: true`
      come l'informativa: niente cornice, nessuna sessione da avere. Il gettone sta nel
      percorso, e la pagina lo porta all'API così com'è; il `GET` non cambia niente, perché i
      filtri delle caselle di posta aprono i link da soli. Il perché di ogni scelta sta in
      testa alla vista.
    */
    path: '/risposta/:token',
    name: 'risposta-invito',
    component: () => import('@/views/auth/RispostaInvitoView.vue'),
    meta: { pubblica: true, titolo: 'Risposta all’invito' },
  },
  {
    /*
      La stanza di una videochiamata, a tutto schermo (`spec/videochiamate.md`). `pubblica` per
      necessità e non perché sia aperta: il PC di una saletta **non ha** una sessione del
      Cruscotto ed entra con il gettone dell'account di sala (`?sala=1`); è la vista a decidere
      con quale titolo parlare al server, e chi non ha né l'uno né l'altro lo manda all'ingresso
      con la destinazione in tasca, come farebbe la guardia. Niente cornice: una galleria di
      volti con la barra laterale accanto è metà schermo buttata.
    */
    path: '/chiamata/:codice',
    name: 'chiamata',
    component: () => import('@/views/videochiamate/StanzaView.vue'),
    meta: { pubblica: true, titolo: 'Videochiamata' },
  },
  {
    /*
      L'ospite esterno, dal link personale nella mail: nessun account, tre lingue, la sala
      d'attesa. Pubblica come la risposta a un invito, e per la stessa ragione: chi la apre
      non è dei nostri.
    */
    path: '/ospite/:segreto',
    name: 'ospite-videochiamata',
    component: () => import('@/views/videochiamate/OspiteView.vue'),
    meta: { pubblica: true, titolo: 'Videochiamata' },
  },
  {
    /*
      «Beste Sale»: l'app dei PC delle sale riunioni, che fa solo le chiamate — l'accesso
      dell'account di sede, la postazione, le chiamate di oggi. Ha una sessione sua (gettone
      `type: 'sala'`, in `localStorage`) che non è quella del Cruscotto e non deve mescolarsi con
      lei: quindi pubblica, e senza cornice. È anche una PWA (`public/sale.webmanifest`).
    */
    path: '/sala',
    name: 'sala-console',
    component: () => import('@/views/videochiamate/BesteSaleView.vue'),
    meta: { pubblica: true, titolo: 'Beste Sale' },
  },
  {
    /*
      Il widget del desktop (2026-10-29): quel che l'app Beste Sale mostra di base, una scheda nell'angolo
      che si anima 5 minuti prima di una chiamata della sala. Stessa sessione della console.
    */
    path: '/sala/widget',
    name: 'sala-widget',
    component: () => import('@/views/videochiamate/SalaWidgetView.vue'),
    meta: { pubblica: true, titolo: 'Beste Sale' },
  },
  {
    /*
      «/SALE» scritto a mano il 2026-10-29 portava al login delle persone, dove l'account della sala non
      esiste: tre tentativi falliti prima di capire. Il plurale porta alla console delle sale.
    */
    path: '/sale',
    redirect: { name: 'sala-console' },
  },
  {
    path: '/cambia-password',
    name: 'cambia-password',
    component: () => import('@/views/auth/CambioPasswordView.vue'),
    // Nessun permesso e nessun rimando: è la schermata in cui si viene mandati
    // *proprio perché* non si può fare altro.
    meta: { permessi: [], titolo: 'Cambia password', senzaCornice: false },
  },
  {
    path: '/profilo',
    name: 'profilo',
    component: () => import('@/views/ProfiloView.vue'),
    meta: { permessi: [], titolo: 'Il mio profilo' },
  },
  {
    /*
      Le presenze di linea: **una PWA a sé**, che sui tablet delle maestre si installa e apre
      solo questa schermata.

      `senzaCornice` perché è proprio la richiesta — «la PWA mi serve che abbia anche solo
      quella vista» — e perché una barra laterale piena di voci che quella persona non può
      aprire sarebbe la stessa porta murata in bella vista del primo avvio qui sopra.

      `permessi: []` **non** vuol dire «aperta a tutti**: vuol dire che il diritto non è del
      ruolo. Chi apre questa pagina senza sorvegliare nessuna linea riceve un rifiuto
      dall'API, perché guardare una linea è un'**appartenenza** e non un permesso del
      catalogo — come per i responsabili di reparto. Un permesso qui direbbe *che* può
      guardare senza dire *quali*, e quali è tutto.

      Non sta in `config/navigation.ts` per la ragione del primo avvio: non è una voce di
      menù. Chi la usa ci arriva dall'icona sul tablet, non da una barra laterale.
    */
    path: '/linee',
    name: 'linee',
    component: () => import('@/views/linee/PresenzeDiLineaView.vue'),
    meta: { permessi: [], titolo: 'Presenze di linea', senzaCornice: true },
  },

  {
    /*
      Il collegamento scritto nelle note dell'impegno sul calendario del telefono (Cosimo,
      2026-10-22: *«un magiclink che permette di vedere le informazioni della riunione attraverso
      la app se la apro dal cellulare o attraverso il sito Internet se lo apro dal PC … con le
      autorizzazioni che ho con l'account che uso»*). Lo scrive `note-del-calendario.ts` nel
      server; dal telefono lo apre l'app, quando il dominio le è associato, e da qui il Cruscotto.

      Un rimando e non una pagina: la riunione si apre dove si apre sempre, nel quadro di
      «Riunioni» (`?riunione=`), che la legge dal server con i permessi di chi è entrato. Il
      rimando avviene **prima** della guardia, quindi chi non è entrato passa dall'accesso e poi
      arriva lì (`vai`), e chi non può vederla riceve dal server il rifiuto di sempre.
    */
    path: '/apri/riunione/:id(\\d+)',
    name: 'apri-riunione',
    redirect: (to) => ({ name: 'riunioni', query: { riunione: String(to.params.id) } }),
  },

  {
    /*
      Il collegamento a una videochiamata scritto nelle notifiche e nel «Copia link» della scheda
      (`ChiamataVista.link` = `<base>/apri/chiamata/<codice>`, 2026-10-26). Sul telefono con l'app
      installata lo apre **l'app** — `/apri/*` è dichiarato ai due sistemi — e qui, nel browser,
      porta alla stanza. Un rimando con `replace` e non una pagina: «indietro» dalla stanza non
      deve ripassare di qui, se no rimbalza dentro la stanza da cui si stava uscendo. Stessa
      forma di `/apri/riunione/:id`. Il link dell'ospite (`/ospite/:segreto`) resta solo web.
    */
    path: '/apri/chiamata/:codice',
    name: 'apri-chiamata',
    redirect: (to) => ({ name: 'chiamata', params: { codice: String(to.params.codice) }, replace: true }),
    /*
      Pubblica come la stanza a cui porta (2026-10-27): al primo caricamento `onSessionePersa`
      guarda **questo** indirizzo, non quello della stanza, e senza il segno il PC di una sala —
      che una sessione del Cruscotto non ce l'ha — finiva all'ingresso con «sessione scaduta»
      invece che nella chiamata. Cosimo: «devo aprire le call anche cliccando sul link».
    */
    meta: { pubblica: true },
  },

  {
    /*
      L'indirizzo dentro il QR della pagina d'ingresso (2026-10-28). Sul telefono con l'app lo apre
      **l'app** (`/apri/*` dichiarato ai due sistemi) e chiede la conferma; qui arriva solo chi lo
      apre nel browser — app non installata, o la fotocamera che non passa il collegamento — e la
      pagina gli dice come fare, invece di una pagina vuota.
    */
    path: '/apri/accesso/:codice',
    name: 'apri-accesso',
    component: () => import('@/views/auth/ApriAccessoView.vue'),
    meta: { pubblica: true, titolo: 'Entra con l’app' },
  },

  ...rotteDelMenu,

  {
    path: '/non-autorizzato',
    name: 'non-autorizzato',
    component: () => import('@/views/errori/NonAutorizzatoView.vue'),
    meta: { permessi: [], titolo: 'Non autorizzato' },
  },
  {
    // `pathMatch` e non un redirect alla home: un indirizzo sbagliato deve
    // *dirlo*. Rimandare al cruscotto in silenzio fa credere di aver cliccato
    // male, e nasconde i collegamenti rotti a chi potrebbe ripararli.
    path: '/:pathMatch(.*)*',
    name: 'non-trovato',
    component: () => import('@/views/errori/NonTrovatoView.vue'),
    meta: { permessi: [], titolo: 'Pagina non trovata' },
  },
];

export const router = createRouter({
  history: createWebHistory(),
  routes,

  /**
   * Dove si trova la pagina dopo un cambio di rotta.
   *
   * Tornare indietro deve restituire **la posizione che c'era**: chi scorre un
   * elenco di cinquecento dipendenti, apre una scheda e torna indietro non deve
   * ricominciare dall'alto. È la differenza fra una navigazione e un
   * ricaricamento mascherato.
   *
   * L'ancora ha la precedenza, e usa `scroll-padding-top` dalle fondamenta per
   * non finire sotto la barra fissa.
   */
  scrollBehavior(_to, _from, salvata) {
    if (salvata) return salvata;
    return { top: 0, behavior: 'smooth' };
  },
});

/**
 * La destinazione chiesta dall'indirizzo, ma solo se è **una nostra pagina**.
 *
 * Un `dove.startsWith('/')` sembra bastare e non basta: `//evil.com` comincia
 * per `/` e il browser lo legge come indirizzo assoluto verso un altro host, di
 * cui eredita lo schema dal nostro. Non serve nemmeno un dominio somigliante —
 * basta mandare a una persona il collegamento **vero** al nostro ingresso con
 * `?vai=//evil.com`: entra sul dominio giusto, che resta giusto nella barra per
 * tutto il tempo in cui lo guarda, e un istante dopo si ritrova su un server
 * altrui davanti a un «sessione scaduta, rientra» imitato bene. La seconda
 * barra è tutta la differenza, e `/\` va rifiutato insieme a lei perché più di
 * un browser lo normalizza allo stesso modo.
 *
 * I caratteri di controllo cadono per primi, e per la stessa ragione: nella
 * lettura di un indirizzo tabulatori e a capo vengono **tolti**, quindi un
 * `/<tab>/evil.com` arriverebbe qui con l'aria di un percorso interno e
 * tornerebbe a essere `//evil.com` un attimo più tardi.
 *
 * L'ultima parola tocca a chi sa davvero quali pagine esistono: se `resolve`
 * finisce sul non trovato, quella non è una destinazione. Il rimando dopo
 * l'accesso è l'unico posto in cui un 404 non va mostrato — chi ha appena
 * scritto la password non ha sbagliato niente, e il cruscotto è una risposta
 * migliore di una pagina d'errore.
 *
 * Restituisce `null` — non solleva e non devia da sé — perché chi chiama sa
 * qual è la sua destinazione normale, e questa funzione non deve saperlo.
 */
export function destinazioneInterna(grezzo: unknown): string | null {
  if (typeof grezzo !== 'string') return null;
  // `\p{Cc}` invece dell'intervallo scritto a mano: dice la stessa cosa per
  // nome anziché per codice, copre anche i controlli C1, e non fa scattare
  // `no-control-regex` — che sull'intervallo scattava a ragione, perché un
  // carattere di controllo messo lì dentro è quasi sempre un refuso, mentre
  // qui è proprio la cosa che si vuole intercettare.
  if (/\p{Cc}/u.test(grezzo)) return null;
  if (grezzo[0] !== '/' || grezzo[1] === '/' || grezzo[1] === '\\') return null;

  const risolta = router.resolve(grezzo);
  return risolta.name === 'non-trovato' ? null : risolta.fullPath;
}

/**
 * La guardia unica.
 *
 * Cinque domande, in quest'ordine, e l'ordine è il punto:
 *
 *  1. **Sappiamo chi è?** Alla prima navigazione no, e va chiesto al server.
 *     Farlo qui e non nel componente significa che nessuna schermata si disegna
 *     mai in uno stato in cui non sa se l'utente esiste.
 *  2. **Esiste qualcuno, in questo impianto?** Solo se la risposta alla prima è
 *     "nessuno". Un utente autenticato **è** la prova che la tabella degli
 *     utenti non è vuota, e per lui la domanda costa zero richieste: è tutta la
 *     ragione per cui questa sta *dopo* la verifica di sessione e non prima.
 *     Sul percorso normale — quello di ogni utente, ogni giorno — il primo
 *     avvio non aggiunge niente a niente.
 *  3. **È entrato?** Se no, all'ingresso, **con la destinazione in tasca**: chi
 *     apre un collegamento a una nota spese e trova la schermata d'accesso deve
 *     ritrovarsi sulla nota spese subito dopo, non sul cruscotto.
 *  4. **Deve cambiare password?** Senza scampo: qualunque destinazione diversa
 *     dal cambio password viene deviata. Il server rifiuterebbe comunque, ma
 *     sbattere contro un rifiuto non è una spiegazione.
 *  5. **Ha il permesso?** Se no, un rifiuto scritto — non una pagina bianca e
 *     non un rimando muto al cruscotto, che lascerebbe credere a un guasto.
 *
 * (Le due di mezzo erano elencate nell'ordine inverso rispetto a come il codice
 * le pone davvero. Non cambiava niente — non si può dover cambiare password
 * senza essere entrati — ma un elenco che si presenta dicendo che l'ordine è il
 * punto non può poi essere l'unica parte che non lo rispetta.)
 */
/*
  Un Cruscotto nuovo è uscito mentre questa scheda era aperta (`lib/versione-nuova.ts`): al
  cambio di pagina si carica quello, all'indirizzo dove si stava andando. Cambiare pagina butta
  via la pagina di prima comunque, quindi qui non si perde niente; e senza questo, chi tiene il
  Cruscotto aperto tutto il giorno lavora col codice della mattina (Sabrina Brabanti, 2026-10-21,
  tre rifiuti dopo che la cura era già in servizio). Non alla prima navigazione: lì il codice è
  appena arrivato. E non quando cambia solo la query o l'àncora: molte pagine scrivono i filtri
  nell'indirizzo, e quello è lavorare sulla stessa pagina, non lasciarla.
*/
router.beforeEach((to, from) => {
  if (!versioneNuova.value || from.matched.length === 0 || to.path === from.path) return true;
  window.location.assign(router.resolve(to).href);
  return false;
});

router.beforeEach(async (to) => {
  const auth = useAuthStore();

  if (!auth.pronto) {
    try {
      await auth.verificaSessione();
    } catch {
      // Server irraggiungibile: si prosegue senza sessione. La schermata
      // d'ingresso è l'unica che può funzionare, e lo dirà con parole sue.
      //
      // Da quando esiste il primo avvio quella frase ha un'eccezione, e vale la
      // pena scriverla perché è il caso che sembra chiedere una deviazione e non
      // la sopporta: su un impianto appena installato nemmeno l'ingresso può
      // funzionare, perché non c'è nessuna credenziale da scriverci. Ma da qui
      // non si sa quale dei due impianti si abbia davanti — il server tace su
      // entrambe le domande — e mandare al primo avvio sulla base di un silenzio
      // manderebbe lì tutti gli utenti di un impianto in piena attività. Chi sta
      // installando vedrà l'ingresso per il tempo di un ritentativo; il
      // controllo qui sotto riparte a ogni rotta finché `pronto` è falso.
    }
  }

  // Chi è dentro non ha bisogno che glielo si chieda: la tabella degli utenti
  // non è vuota, la prova è lui. Segnarlo è ciò che rende il primo avvio
  // gratuito per tutti gli altri — dopo il primo accesso, nessuno paga più una
  // richiesta per una domanda la cui risposta non può più cambiare.
  if (auth.autenticato) {
    segnaImpiantoConfigurato();
  } else {
    const impianto = await leggiStatoImpianto();

    // La porta è aperta: ci si va da qualunque indirizzo si fosse partiti,
    // compreso l'ingresso — che su un impianto vuoto è la schermata che dà
    // meno di tutte. Il controllo su `to.name` è ciò che tiene fuori il ciclo:
    // senza, la deviazione rimanderebbe alla rotta che l'ha appena prodotta.
    if (impianto === 'da-configurare' && to.name !== 'primo-avvio') {
      return { name: 'primo-avvio' };
    }

    // La porta è chiusa, e si sa: il primo avvio non è più una schermata.
    // Mostrarne il modulo vorrebbe dire far compilare un'anagrafica intera a
    // qualcuno per fargliela rifiutare dal server alla riga dell'invio.
    if (impianto === 'configurato' && to.name === 'primo-avvio') {
      return { name: 'entra' };
    }

    // `ignoto`: nessuna deviazione, in nessuno dei due versi — non si devia
    // sulla base di una cosa che non si sa. Chi puntava al primo avvio ci
    // resta, e ci resta di proposito: è il solo posto in cui la domanda verrà
    // rifatta da chi ha modo di raccontarne l'esito, mentre spedirlo
    // all'ingresso lo metterebbe davanti all'unica schermata che, se l'impianto
    // è davvero vuoto, non ha niente da offrirgli.
  }

  if (to.meta.pubblica === true) {
    // Già dentro e diretto a una schermata di soglia: non ha senso mostrargli i
    // campi. Vale per l'ingresso come per il primo avvio, e la destinazione
    // ripescata da `vai` serve al primo dei due — ma passarla anche al secondo
    // non fa danno e non aggiunge un secondo rimando da tenere allineato.
    //
    // ⚠ **Solo le due soglie**, non ogni pagina pubblica. L'informativa e la risposta a un
    // invito sono pubbliche perché si aprono *anche* senza account, non perché siano per chi
    // non è dentro: i link «Partecipo / Non partecipo» partono anche ai dipendenti
    // (`risposte-da-posta.service`, `userId`), e chi ha il Cruscotto aperto nello stesso
    // browser — col login che non scade — cliccandoli finiva sulla home, e dalla mail non
    // poteva rispondere affatto.
    const soglia = to.name === 'entra' || to.name === 'primo-avvio';
    if (soglia && auth.autenticato && !auth.deveCambiarePassword) {
      return destinazioneInterna(to.query.vai) ?? { name: 'cruscotto' };
    }

    // Autenticato ma con la password da cambiare, ed è il buco che il ramo qui
    // sopra lascia aperto. Sull'ingresso lasciar passare è sempre andato bene:
    // i campi non fanno danno, e uscire e rientrare è una via d'uscita
    // legittima. Sul primo avvio no — è il modulo che crea un amministratore, e
    // questa è l'unica strada da cui lo si potrebbe raggiungere da dentro.
    if (auth.autenticato && to.name === 'primo-avvio') {
      return { name: 'cambia-password' };
    }

    return true;
  }

  if (!auth.autenticato) {
    return { name: 'entra', query: { vai: to.fullPath } };
  }

  if (auth.deveCambiarePassword && to.name !== 'cambia-password') {
    return { name: 'cambia-password' };
  }

  /*
    Le rotte del menu si chiedono a `voceVisibile`, che è lo stesso padrone del menu e delle
    scorciatoie della home. Le altre — cambia password, il mio profilo, le presenze di linea —
    non sono voci di menu e portano i propri `permessi` in `meta`, che è il caso qui sotto.
  */
  if (typeof to.meta.chiave === 'string') {
    // `voceApribile` e non `voceVisibile`: sono la stessa domanda tranne dove la condizione
    // chiude solo la voce nel menu — la campanella porta a «Comunicazioni» anche chi non scrive.
    if (!voceApribile(to.meta.chiave, auth)) {
      return { name: 'non-autorizzato', query: { da: to.fullPath } };
    }
  } else {
    const richiesti = to.meta.permessi ?? [];
    if (richiesti.length > 0 && !auth.canAny(...richiesti)) {
      return { name: 'non-autorizzato', query: { da: to.fullPath } };
    }
  }

  /*
    ⚠ **Qui c'era la seconda metà della guardia, e adesso sta dentro `voceVisibile`.**

    Fino al 2026-10-03 la condizione che non è un permesso la leggeva soltanto il menu: chi
    digitava `/valutazioni` senza essere valutatore apriva la pagina, che poi non caricava
    niente. Era un difetto senza conseguenze visibili finché la condizione serviva solo ad
    **aggiungere** una voce a pochi; da quando serve anche a **toglierla** — le prenotazioni ai
    responsabili, la logistica dove non si fa — una rotta che si apre comunque contraddice
    proprio la decisione che l'ha nascosta. Dal 2026-10-14 le due regole si chiedono insieme e
    in un posto solo, e la risposta a chi arriva è la stessa delle due: questa pagina non è per
    lui. Quale delle due l'abbia fermato è una distinzione nostra.
  */

  return true;
});

/**
 * Il titolo della scheda del browser.
 *
 * Sembra un dettaglio; non lo è per chi tiene aperte otto schede del pannello
 * — cioè per chiunque approvi qualcosa. `afterEach` e non `beforeEach`: il
 * titolo deve cambiare quando la pagina è cambiata davvero, non quando si è
 * solo tentato di andarci.
 */
router.afterEach((to) => {
  document.title = to.meta.titolo ? `${to.meta.titolo} · BesteHR` : 'BesteHR';
});

/**
 * Sessione persa mentre si lavorava.
 *
 * Il deposito si è già svuotato da solo (ci pensa lo stesso richiamo). Qui
 * resta da portare la persona all'ingresso **conservando dov'era**, così che
 * dopo aver rifatto l'accesso si ritrovi sulla stessa schermata invece che
 * all'inizio. La guardia sull'indirizzo corrente evita di accumulare voci
 * inutili nella cronologia se il richiamo scatta più volte.
 */
onSessionePersa(() => {
  const corrente = router.currentRoute.value;
  if (corrente.name === 'entra') return;
  /*
    Le pagine pubbliche non si interrompono: chi le apre **non ha** una sessione, e non
    l'ha persa. Senza questo controllo l'informativa privacy — che per gli store deve
    aprirsi a chi non ha un account — durava il tempo di un `/auth/me` a 401 e poi spariva
    dentro l'ingresso.

    Si guarda l'indirizzo **del browser** e non `router.currentRoute`, e la differenza non
    è un dettaglio: al primo caricamento questo richiamo scatta *dentro* la guardia, mentre
    la navigazione verso `/privacy` non è ancora finita, e in quel momento la rotta
    corrente è ancora quella iniziale — infatti il rimando portava `vai=/`, non
    `vai=/privacy`. Misurato il 2026-09-22 leggendo le navigazioni una per una, dopo che la
    prima cura (`corrente.meta.pubblica`) non aveva cambiato niente.
  */
  if (router.resolve(window.location.pathname).meta.pubblica === true) return;
  void router.replace({ name: 'entra', query: { vai: corrente.fullPath, scaduta: '1' } });
});
